import { supabase } from '../lib/supabaseClient';
import type { Action } from '../types/action';
import { isValidUuid } from '../utils/uuid';

const LOCAL_TO_ENTERPRISE_STATUS: Record<string, string> = {
  todo: 'pending',
  doing: 'in_progress',
  done: 'completed',
  canceled: 'cancelled',
};

/** Garante projectId e IDs UUID válidos para persistência cross-módulo. */
export function normalizeActionsForProject(actions: Action[], projectId: string): Action[] {
  const now = new Date().toISOString();
  return actions.map((action) => ({
    ...action,
    id: isValidUuid(action.id) ? action.id : crypto.randomUUID(),
    projectId: action.projectId ?? projectId,
    createdAt: action.createdAt ?? now,
    updatedAt: action.updatedAt ?? now,
  }));
}

function parseDueDate(prazo?: string, deadline?: string): string | null {
  const raw = prazo ?? deadline;
  if (!raw) return null;
  if (raw.includes('/')) {
    const [d, m, y] = raw.split('/');
    if (!d || !m || !y) return null;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return raw.slice(0, 10);
}

function priorityFromImpact(impact?: number): string {
  if (impact == null || Number.isNaN(impact)) return 'medium';
  if (impact <= 1) return 'low';
  if (impact >= 3) return 'high';
  return 'medium';
}

function buildActionMetadata(action: Action): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    ...((action as { metadata?: Record<string, unknown> }).metadata ?? {}),
  };
  if (action.linkedNodeId) metadata.linkedNodeId = action.linkedNodeId;
  if (action.sourceComponentId) metadata.sourceComponentId = action.sourceComponentId;
  if (action.linkedPostItId) metadata.linkedPostItId = action.linkedPostItId;
  if (action.originSource) metadata.source = action.originSource;
  if (action.esforco != null) metadata.matrixEffort = action.esforco;
  if (action.impacto != null) metadata.matrixImpact = action.impacto;
  if (action.responsavel) metadata.assignedName = action.responsavel;
  if (action.actionPlanId) metadata.actionPlanId = action.actionPlanId;

  const impactValue = action.impact ?? action.impacto;
  const effortValue = action.effort ?? action.esforco;
  if (typeof effortValue === 'number') metadata.effort = effortValue;
  if (typeof impactValue === 'number') metadata.impact = impactValue;

  return metadata;
}

function actionToSupabaseRow(
  action: Action,
  organizationId: string,
  projectId: string,
  userId?: string,
): Record<string, unknown> {
  const metadata = buildActionMetadata(action);
  const impactValue = action.impact ?? action.impacto;

  // effort/impact ficam em metadata — colunas dedicadas podem não existir no schema Brasil Flow.
  return {
    id: action.id,
    organization_id: organizationId,
    project_id: projectId,
    title: action.title?.trim() || 'Ação sem título',
    description: action.description ?? null,
    status: LOCAL_TO_ENTERPRISE_STATUS[action.status] ?? 'pending',
    priority: priorityFromImpact(
      typeof impactValue === 'number' ? impactValue : undefined,
    ),
    due_date: parseDueDate(action.prazo, action.deadline),
    assigned_to: action.responsibleUserId ?? (action as { responsavel_id?: string }).responsavel_id ?? null,
    created_by: userId ?? null,
    updated_at: action.updatedAt ?? new Date().toISOString(),
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
  };
}

function actionToMinimalSupabaseRow(
  action: Action,
  organizationId: string,
  projectId: string,
  userId?: string,
): Record<string, unknown> {
  const impactValue = action.impact ?? action.impacto;
  return {
    id: action.id,
    organization_id: organizationId,
    project_id: projectId,
    title: action.title?.trim() || 'Ação sem título',
    description: action.description ?? null,
    status: LOCAL_TO_ENTERPRISE_STATUS[action.status] ?? 'pending',
    priority: priorityFromImpact(
      typeof impactValue === 'number' ? impactValue : undefined,
    ),
    created_by: userId ?? null,
  };
}

function isSchemaColumnMismatch(message: string): boolean {
  return /column|schema cache|Could not find/i.test(message);
}

/** Sincroniza ações do projeto para a tabela `actions` (Central de Ações / Minha Mesa). */
export async function syncProjectActionsToSupabase(
  actions: Action[],
  organizationId: string,
  projectId: string,
  userId?: string,
): Promise<{ error: Error | null; synced: number }> {
  if (!isValidUuid(projectId) || !organizationId) {
    return { error: new Error('Projeto ou organização inválidos para sincronizar ações.'), synced: 0 };
  }

  const normalized = normalizeActionsForProject(actions, projectId);

  if (normalized.length === 0) {
    const { error } = await supabase
      .from('actions')
      .delete()
      .eq('organization_id', organizationId)
      .eq('project_id', projectId);
    if (error) {
      console.warn('[actionSync] Limpeza de ações ignorada:', error.message);
    }
    return { error: null, synced: 0 };
  }

  const payloads = normalized.map((action) =>
    actionToSupabaseRow(action, organizationId, projectId, userId),
  );

  let { error } = await supabase
    .from('actions')
    .upsert(payloads, { onConflict: 'id' });

  if (error && isSchemaColumnMismatch(error.message)) {
    const compatibilityPayloads = normalized.map((action) =>
      actionToMinimalSupabaseRow(action, organizationId, projectId, userId),
    );
    ({ error } = await supabase
      .from('actions')
      .upsert(compatibilityPayloads, { onConflict: 'id' }));
  }

  if (error) {
    return { error: new Error(error.message), synced: 0 };
  }

  const keepIds = new Set(payloads.map((row) => row.id as string));
  const { data: existing } = await supabase
    .from('actions')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('project_id', projectId);

  const orphanIds = (existing ?? [])
    .map((row) => row.id as string)
    .filter((id) => !keepIds.has(id));

  if (orphanIds.length > 0) {
    await supabase.from('actions').delete().in('id', orphanIds);
  }

  return { error: null, synced: normalized.length };
}

/**
 * Upserts a plan action record to the `actions` Supabase table
 * so it can be queried cross-project by assigned user (Minha Mesa).
 */
export async function syncPlanActionToSupabase(
  action: Action,
  organizationId: string,
  projectId: string,
  responsibleUserId: string,
): Promise<void> {
  if (!organizationId || !projectId || !responsibleUserId) return;

  const statusMap: Record<string, string> = {
    todo: 'pending',
    doing: 'in_progress',
    done: 'completed',
    canceled: 'cancelled',
  };

  const priorityMap: Record<number, string> = {
    1: 'low',
    2: 'medium',
    3: 'high',
  };

  const payload = {
    id: action.id,
    organization_id: organizationId,
    project_id: projectId,
    title: action.title,
    description: action.description ?? null,
    status: statusMap[action.status] ?? 'pending',
    priority: action.impacto != null ? (priorityMap[action.impacto] ?? 'medium') : 'medium',
    due_date: action.prazo ? (() => {
      // Handle DD/MM/YYYY format
      if (action.prazo.includes('/')) {
        const [d, m, y] = action.prazo.split('/');
        return `${y}-${m}-${d}`;
      }
      return action.prazo.slice(0, 10);
    })() : null,
    assigned_to: responsibleUserId,
    updated_at: new Date().toISOString(),
  };

  await supabase
    .from('actions')
    .upsert(payload, { onConflict: 'id' });
}

/**
 * Loads plan actions assigned to a specific user from the `actions` table.
 * Used by Minha Mesa to show cross-project assigned actions.
 */
export async function loadMyPlanActions(
  userId: string,
  organizationId: string,
): Promise<AssignedPlanAction[]> {
  if (!userId || !organizationId) return [];

  const { data, error } = await supabase
    .from('actions')
    .select('id, title, status, due_date, project_id, priority, created_at, updated_at')
    .eq('organization_id', organizationId)
    .eq('assigned_to', userId)
    .not('status', 'in', '(completed,cancelled)')
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(50);

  if (error || !data) return [];

  return data.map((row: Record<string, unknown>) => ({
    id: row.id as string,
    title: row.title as string,
    status: row.status as string,
    dueDate: row.due_date as string | null,
    projectId: row.project_id as string | null,
    priority: row.priority as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }));
}

export interface AssignedPlanAction {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  projectId: string | null;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
}
